import { Box } from "@mui/material";
import React from "react";
import ItemDetailViewSection, {
  Item,
  ItemDetail,
} from "../../../common/item/ItemDetailViewSection";
import ItemsDetailViewSection, {
  ItemsDetail,
} from "../../../common/item/ItemsDetailViewSection";
import ScrollSpyTabs from "../../../common/nav/ScrollSpyTabs";
import { Data } from "../../../store/merchant/types";
import moment from "moment-timezone";
import { connect } from "react-redux";
import { ConfigPropExtenderHoc } from "../../../common/StaticConfigProvider";
import { get } from "lodash";
import useUserAccess from "../../../effects/useUserAccess";

interface MerchantViewDetailProps {
  merchantData: Data;
  toolTips?: { merchants?: { options?: Record<string, string> } };
}

const MerchantViewDetail = (props: MerchantViewDetailProps) => {
  const { attributes: at, scheduleJob } = props.merchantData;
  const access = useUserAccess();
  const SectionAccess = get(access, "Merchant.SectionAccess", {});
  const MerchantInfoAccess = get(SectionAccess, "MerchantInfo.Access", {
    R: false,
    U: false,
  });
  const ProcessorAccess = get(SectionAccess, "Processor.Access", {
    R: false,
    U: false,
  });
  const ContactAccess = get(SectionAccess, "Contact.Access", {
    R: false,
    U: false,
  });
  const SurchargeAccess = get(SectionAccess, "Surcharge.Access", {
    R: false,
    U: false,
  });
  const ConfigurationAccess = get(SectionAccess, "Configuration.Access", {
    R: false,
    U: false,
  });

  // DATA MAPPING
  const merchantInfo: Item[] = [
    { fieldName: "Name", fieldValue: at.Name },
    { fieldName: "Address 1", fieldValue: at.Address1 },
    { fieldName: "Address 2", fieldValue: at.Address2 },
    { fieldName: "Suburb", fieldValue: at.Suburb },
    { fieldName: "State", fieldValue: at.State },
    { fieldName: "Postcode", fieldValue: at.Postcode },
    { fieldName: "MCC", fieldValue: at.MCC },
    { fieldName: "MID", fieldValue: at.MID },
    { fieldName: "Note", fieldValue: at.Note },
  ];

  const processorItems =
    (at.Processor?.map((prc) => ({
      sectionName: prc.ProcessorName,
      itemData: [
        { fieldName: "Processor Name", fieldValue: prc.ProcessorName },
        { fieldName: "MID", fieldValue: prc.MID },
      ],
      additionalData: prc.AdditionalData,
    })) as ItemDetail[]) || [];

  const merchantProcessors: ItemsDetail = {
    sectionName: "Processor",
    itemsData: processorItems,
  };

  const merchantContacts: ItemsDetail = {
    sectionName: "Contacts",
    itemsData: at?.Contact?.map((contact, i) => ({
      sectionName: `${i == 0 ? "Primary" : i == 1 ? "Secondary" : ""} Contact`,
      itemData: [
        { fieldName: "First Name", fieldValue: contact.First },
        { fieldName: "Last Name", fieldValue: contact.Last },
        { fieldName: "Mobile", fieldValue: contact.Mobile },
        { fieldName: "Phone", fieldValue: contact.Phone },
        { fieldName: "Email", fieldValue: contact.email },
      ],
    })),
  };

  const merchantSurcharge: ItemsDetail = {
    sectionName: "Surcharge",
    itemsData: [
      {
        sectionName: "Surcharge ",
        itemData: [
          {
            fieldName: "Surcharge",
            fieldValue: at.Surcharge,
          },
          {
            fieldName: "Surcharge on tips",
            fieldValue: at.SurchargeTip,
          },
        ],
      },
      {
        sectionName: "Surcharge Percentage",
        itemData:
          (at.SurchargePercentage &&
            at.SurchargePercentage.map(
              (per) =>
                ({
                  fieldName: per.CardType,
                  fieldValue: (per.Percentage || "").toString() + "%",
                } as Item)
            )) ||
          [],
      },
    ],
  };

  const merchantConfiguration: ItemsDetail = {
    sectionName: "Configuration",
    itemsData: [
      {
        sectionName: "Auto Settlement",
        itemData: [
          {
            fieldName: "Auto Settlement",
            fieldValue: scheduleJob?.enabled,
            tooltip: get(
              props,
              `toolTips.merchants.options.configuration.AutoSettlement`
            ),
          },
          {
            fieldName: "Auto-Settle Time",
            fieldValue: scheduleJob?.timeSchedule,
            tooltip: get(
              props,
              `toolTips.merchants.options.configuration.AutoSettlementTime`
            ),
          },
          {
            fieldName: "Time Zone",
            fieldValue: scheduleJob?.timezone,
            tooltip: get(
              props,
              `toolTips.merchants.options.configuration.AutoSettlementTimezone`
            ),
          },
          {
            fieldName: "Last Execution",
            fieldValue: moment(scheduleJob?.lastExecution).format(
              "DD/MM/YYYY HH:MM:SS"
            ),
          },
          {
            fieldName: "Last Completed",
            fieldValue: moment(scheduleJob?.lastCompleted).format(
              "DD/MM/YYYY HH:MM:SS"
            ),
          },
        ],
      },
      {
        sectionName: "Other",
        itemData: [
          {
            fieldName: "Cash Out Daily Limit Enabled",
            fieldValue: at.CashOutDailyLimitEnabled,
          },
          {
            fieldName: "Cash Out Daily Limit",
            fieldValue: (at.CashOutDailyLimit || "").toString(),
          }
        ]
      }
    ],
  };

  return (
    <Box>
      <ScrollSpyTabs
        tabsInScroll={[
          {
            label: "Merchant Info",
            component: (
              <ItemDetailViewSection
                sectionName="Merchant Info"
                itemData={merchantInfo}
                isRestricted={!MerchantInfoAccess.R}
              />
            ),
          },
          {
            label: "Processor",
            component: (
              <ItemsDetailViewSection
                sectionName={merchantProcessors.sectionName}
                itemsData={merchantProcessors.itemsData}
                isRestricted={!ProcessorAccess.R}
              />
            ),
          },
          {
            label: "Contacts",
            component: (
              <ItemsDetailViewSection
                sectionName={merchantContacts.sectionName}
                itemsData={merchantContacts.itemsData}
                isRestricted={!ContactAccess.R}
              />
            ),
          },
          {
            label: "Surcharge",
            component: (
              <ItemsDetailViewSection
                sectionName={merchantSurcharge.sectionName}
                itemsData={merchantSurcharge.itemsData}
                isRestricted={!SurchargeAccess.R}
              />
            ),
          },
          {
            label: "Configuration",
            component: (
              <ItemsDetailViewSection
                sectionName={merchantConfiguration.sectionName}
                itemsData={merchantConfiguration.itemsData}
                isRestricted={!ConfigurationAccess.R}
                lastItem
              />
            ),
          },
        ]}
      />
    </Box>
  );
};

const MerchantViewDetailHoc = connect(
  null,
  null,
  (_stateProps, _dispatchProps, ownProps: MerchantViewDetailProps) => {
    return {
      ...ownProps,
    };
  }
)(MerchantViewDetail);

const ConnectedMerchantViewDetailHoc = (props: MerchantViewDetailProps) => (
  // @ts-ignore
  <ConfigPropExtenderHoc propName="toolTips">
    <MerchantViewDetailHoc {...props} />
  </ConfigPropExtenderHoc>
);

export default ConnectedMerchantViewDetailHoc;
